import React from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?:
        | 'primary'
        | 'secondary'
        | 'ghost'
        | 'outline'
        | 'destructive'
        | 'blue'
        | 'red'
        | 'violet'
        | 'success'
        | 'neutral'
        | 'dark';
    size?: 'xs' | 'sm' | 'md' | 'lg';
    children: React.ReactNode;
    loading?: boolean;
    href?: string;
}

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>((
    { className = '', variant = 'primary', size = 'md', loading = false, children, disabled, href, ...props },
    ref,
) => {
    const base =
        'inline-flex items-center justify-center font-semibold transition-all duration-200 ease-out focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none cursor-pointer select-none';

    const variants = {
        // Orange → pink gradient: main CTA. A thin lit top edge and a short, low glow instead of a heavy drop shadow.
        primary:
            'bg-linear-to-r from-orange-500 to-pink-500 text-white ' +
            'shadow-[0_1px_2px_rgba(194,65,12,0.25),0_0_0_1px_rgba(194,65,12,0.12),0_5px_12px_-6px_rgba(236,72,153,0.45),inset_0_1px_0_rgba(255,255,255,0.24)] ' +
            'hover:from-orange-600 hover:to-pink-600 hover:-translate-y-px ' +
            'hover:shadow-[0_2px_4px_rgba(194,65,12,0.25),0_0_0_1px_rgba(194,65,12,0.14),0_9px_18px_-8px_rgba(236,72,153,0.5),inset_0_1px_0_rgba(255,255,255,0.3)] ' +
            'active:translate-y-0 active:scale-[0.98] active:shadow-[0_1px_2px_rgba(194,65,12,0.3),inset_0_2px_3px_rgba(124,45,18,0.25)] ' +
            'focus:ring-orange-400',

        // Soft orange: elevated secondary CTA, tinted top to bottom with a faint lit edge.
        secondary:
            'bg-orange-50 bg-linear-to-b from-orange-50 to-amber-100/70 border border-orange-200/80 text-orange-800 ' +
            'shadow-[0_1px_2px_rgba(234,88,12,0.08),0_4px_10px_-5px_rgba(234,88,12,0.26),inset_0_1px_0_rgba(255,255,255,0.75)] ' +
            'hover:from-orange-100/80 hover:to-amber-100 hover:border-orange-300 hover:text-orange-900 hover:-translate-y-px ' +
            'hover:shadow-[0_2px_4px_rgba(234,88,12,0.1),0_8px_16px_-6px_rgba(234,88,12,0.3),inset_0_1px_0_rgba(255,255,255,0.85)] ' +
            'active:translate-y-0 active:scale-[0.98] active:shadow-[0_1px_2px_rgba(234,88,12,0.1),inset_0_1px_3px_rgba(234,88,12,0.18)] ' +
            'focus:ring-orange-200',

        // Refined ghost: white, crisp border, a whisper of depth that turns violet on hover.
        ghost:
            'bg-white/95 backdrop-blur-sm text-slate-700 border border-slate-200 ' +
            'shadow-[0_1px_2px_rgba(15,23,42,0.05),0_3px_8px_-4px_rgba(15,23,42,0.1)] ' +
            'hover:bg-violet-50/60 hover:text-violet-800 hover:border-violet-200 hover:-translate-y-px ' +
            'hover:shadow-[0_1px_3px_rgba(99,102,241,0.1),0_6px_14px_-6px_rgba(99,102,241,0.22)] ' +
            'active:translate-y-0 active:scale-[0.98] active:bg-slate-50/80 active:shadow-[0_1px_2px_rgba(15,23,42,0.06)] ' +
            'transition-all duration-200 ease-out ' +
            'focus:ring-slate-200',

        // Soft orange outline: calm at rest, gains only a little depth on hover.
        outline:
            'border border-orange-200 bg-orange-50/70 text-orange-800 ' +
            'shadow-[0_1px_2px_rgba(234,88,12,0.06),0_3px_8px_-4px_rgba(234,88,12,0.18),inset_0_1px_0_rgba(255,255,255,0.7)] ' +
            'hover:-translate-y-px hover:border-orange-300 hover:bg-orange-100/80 hover:text-orange-900 ' +
            'hover:shadow-[0_1px_3px_rgba(234,88,12,0.1),0_7px_14px_-6px_rgba(234,88,12,0.26),inset_0_1px_0_rgba(255,255,255,0.8)] ' +
            'active:translate-y-0 active:scale-[0.98] active:bg-orange-100 active:shadow-[0_1px_2px_rgba(234,88,12,0.1),inset_0_1px_3px_rgba(234,88,12,0.16)] ' +
            'focus:ring-orange-200',

        // Red solid: danger actions, lit from above with a short red glow.
        destructive:
            'bg-red-500 bg-linear-to-b from-red-500 to-red-600 text-white ' +
            'shadow-[0_1px_2px_rgba(153,27,27,0.28),0_0_0_1px_rgba(185,28,28,0.16),0_5px_12px_-6px_rgba(239,68,68,0.45),inset_0_1px_0_rgba(255,255,255,0.2)] ' +
            'hover:from-red-600 hover:to-red-700 hover:-translate-y-px ' +
            'hover:shadow-[0_2px_4px_rgba(153,27,27,0.3),0_0_0_1px_rgba(185,28,28,0.18),0_9px_18px_-8px_rgba(239,68,68,0.5),inset_0_1px_0_rgba(255,255,255,0.25)] ' +
            'active:translate-y-0 active:scale-[0.98] active:from-red-700 active:to-red-700 active:shadow-[0_1px_2px_rgba(153,27,27,0.3),inset_0_2px_3px_rgba(69,10,10,0.3)] ' +
            'focus:ring-red-400',

        // Sky: info / link actions. Tinted top to bottom, thin border, a faint colored lift.
        blue:
            'bg-sky-50 bg-linear-to-b from-sky-50 to-sky-100/80 border border-sky-200 text-sky-800 ' +
            'shadow-[0_1px_2px_rgba(14,165,233,0.08),0_3px_8px_-4px_rgba(14,165,233,0.25),inset_0_1px_0_rgba(255,255,255,0.8)] ' +
            'hover:from-sky-50 hover:to-sky-100 hover:border-sky-300 hover:text-sky-900 hover:-translate-y-px ' +
            'hover:shadow-[0_1px_3px_rgba(14,165,233,0.12),0_7px_14px_-6px_rgba(14,165,233,0.32),inset_0_1px_0_rgba(255,255,255,0.9)] ' +
            'active:translate-y-0 active:scale-[0.98] active:from-sky-100 active:to-sky-100 active:shadow-[0_1px_2px_rgba(14,165,233,0.1),inset_0_1px_3px_rgba(14,165,233,0.2)] ' +
            'focus:ring-sky-200',

        // Rose: soft danger / warning, same finish as the other tinted variants.
        red:
            'bg-rose-50 bg-linear-to-b from-rose-50 to-rose-100/80 border border-rose-200 text-rose-800 ' +
            'shadow-[0_1px_2px_rgba(244,63,94,0.08),0_3px_8px_-4px_rgba(244,63,94,0.25),inset_0_1px_0_rgba(255,255,255,0.8)] ' +
            'hover:from-rose-50 hover:to-rose-100 hover:border-rose-300 hover:text-rose-900 hover:-translate-y-px ' +
            'hover:shadow-[0_1px_3px_rgba(244,63,94,0.12),0_7px_14px_-6px_rgba(244,63,94,0.32),inset_0_1px_0_rgba(255,255,255,0.9)] ' +
            'active:translate-y-0 active:scale-[0.98] active:from-rose-100 active:to-rose-100 active:shadow-[0_1px_2px_rgba(244,63,94,0.1),inset_0_1px_3px_rgba(244,63,94,0.2)] ' +
            'focus:ring-rose-200',

        // Violet: soft premium / brand actions.
        violet:
            'bg-violet-50 bg-linear-to-b from-violet-50 to-violet-100/80 border border-violet-200 text-violet-800 ' +
            'shadow-[0_1px_2px_rgba(139,92,246,0.08),0_3px_8px_-4px_rgba(139,92,246,0.25),inset_0_1px_0_rgba(255,255,255,0.8)] ' +
            'hover:from-violet-50 hover:to-violet-100 hover:border-violet-300 hover:text-violet-900 hover:-translate-y-px ' +
            'hover:shadow-[0_1px_3px_rgba(139,92,246,0.12),0_7px_14px_-6px_rgba(139,92,246,0.32),inset_0_1px_0_rgba(255,255,255,0.9)] ' +
            'active:translate-y-0 active:scale-[0.98] active:from-violet-100 active:to-violet-100 active:shadow-[0_1px_2px_rgba(139,92,246,0.1),inset_0_1px_3px_rgba(139,92,246,0.2)] ' +
            'focus:ring-violet-200',

        // Emerald: soft confirm / success actions.
        success:
            'bg-emerald-50 bg-linear-to-b from-emerald-50 to-emerald-100/80 border border-emerald-200 text-emerald-800 ' +
            'shadow-[0_1px_2px_rgba(16,185,129,0.08),0_3px_8px_-4px_rgba(16,185,129,0.25),inset_0_1px_0_rgba(255,255,255,0.8)] ' +
            'hover:from-emerald-50 hover:to-emerald-100 hover:border-emerald-300 hover:text-emerald-900 hover:-translate-y-px ' +
            'hover:shadow-[0_1px_3px_rgba(16,185,129,0.12),0_7px_14px_-6px_rgba(16,185,129,0.32),inset_0_1px_0_rgba(255,255,255,0.9)] ' +
            'active:translate-y-0 active:scale-[0.98] active:from-emerald-100 active:to-emerald-100 active:shadow-[0_1px_2px_rgba(16,185,129,0.1),inset_0_1px_3px_rgba(16,185,129,0.2)] ' +
            'focus:ring-emerald-200',

        // Refined neutral — white with a soft top-to-bottom shade, a crisp border and layered shadows.
        // Lifts on hover and presses in on click. Secondary actions on premium pages.
        neutral:
            'bg-white bg-linear-to-b from-white to-slate-50 border border-slate-300/70 text-slate-700 ' +
            'shadow-[0_1px_2px_rgba(15,23,42,0.06),0_4px_10px_-4px_rgba(15,23,42,0.14),inset_0_1px_0_rgba(255,255,255,0.95)] ' +
            'hover:-translate-y-0.5 hover:border-slate-400/60 hover:from-white hover:to-slate-100 hover:text-slate-900 ' +
            'hover:shadow-[0_2px_4px_rgba(15,23,42,0.07),0_10px_20px_-6px_rgba(15,23,42,0.2),inset_0_1px_0_rgba(255,255,255,1)] ' +
            'active:translate-y-0 active:scale-[0.98] active:from-slate-50 active:to-slate-100 ' +
            'active:shadow-[0_1px_2px_rgba(15,23,42,0.08),inset_0_1px_3px_rgba(15,23,42,0.1)] ' +
            'focus:ring-slate-300',

        // Refined dark: near-black with a soft top-to-bottom shade, a thin lit edge and a short shadow.
        // The plain bg colour is the floor: without it a missing gradient would leave white text on white.
        dark:
            'bg-slate-900 bg-linear-to-b from-slate-800 to-slate-900 border border-slate-700/80 text-white ' +
            'shadow-[0_1px_2px_rgba(15,23,42,0.22),0_4px_10px_-4px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.12)] ' +
            'hover:-translate-y-px hover:from-slate-700 hover:to-slate-900 hover:border-slate-600 ' +
            'hover:shadow-[0_2px_4px_rgba(15,23,42,0.25),0_8px_16px_-6px_rgba(15,23,42,0.4),inset_0_1px_0_rgba(255,255,255,0.18)] ' +
            'active:translate-y-0 active:scale-[0.98] active:from-slate-900 active:to-slate-900 ' +
            'active:shadow-[0_1px_2px_rgba(15,23,42,0.3),inset_0_2px_4px_rgba(0,0,0,0.35)] ' +
            'focus:ring-slate-400',
    };

    const sizes = {
        xs: 'gap-1 px-2.5 py-1.5 text-xs rounded-lg',
        sm: 'gap-1.5 px-4 py-2 text-sm rounded-xl',
        md: 'gap-2 px-6 py-2.5 text-[15px] rounded-xl',
        lg: 'gap-2 px-8 py-3.5 text-base rounded-2xl',
    };

    const classes = `${base} ${variants[variant]} ${sizes[size]} ${className}`.trim();

    const spinner = (
        <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
        >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
        </svg>
    );

    if (href) {
        return (
            <a className={classes} href={href} ref={ref as React.Ref<HTMLAnchorElement>} {...props}>
                {loading && spinner}
                {children}
            </a>
        );
    }

    return (
        <button className={classes} disabled={disabled || loading} ref={ref as React.Ref<HTMLButtonElement>} {...props}>
            {loading && spinner}
            {children}
        </button>
    );
});

Button.displayName = 'Button';
export default Button;
